import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useEffect } from "react";
import { usePreloadedQuery, useQueryLoader, PreloadedQuery } from "react-relay";

import { useInitializeRound } from "@/screens/JoinBooking/ConfirmPanel/atoms/hooks/useInitializeRound";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display/SkeletonLoader/SkeletonLoader";
import { HStack, VStack } from "@whoosh/design-system/src/components/primitive";

import { getRateOnRoundAtom } from "../../../../atoms/selectors/getRateOnRoundAtom/getRateOnRoundAtom";
import { JoiningUserRow } from "../JoiningUserRow/JoiningUserRow";
import { RateVerificationBanner } from "../RateVerificationBanner/RateVerificationBanner";
import { ConfigureActivityManagementRoundSessionQuery } from "./__generated__/ConfigureActivityManagementRoundSessionQuery.graphql";
import { ConfigureActivityManagementRoundPlayerRate } from "./components/ConfigureActivityManagementRoundPlayerRate/ConfigureActivityManagementRoundPlayerRate";

type ConfigureRoundProps = {
  shareId: string;
  membershipTypeId: string;
  playerCount: number;
  duration: number;
};

export const ConfigureActivityManagementRound = ({
  shareId,
  membershipTypeId,
  playerCount,
  duration,
}: ConfigureRoundProps) => {
  const [queryRef, loadQuery] =
    useQueryLoader<ConfigureActivityManagementRoundSessionQuery>(
      ConfigureRoundSessionQueryString
    );

  useEffect(() => {
    loadQuery(
      {
        shareId,
        playerCount,
        duration,
      },
      { fetchPolicy: "store-and-network" }
    );
  }, [loadQuery, shareId, playerCount, duration]);

  return (
    <Suspense fallback={<SkeletonLoader height={52} width="100%" />}>
      {queryRef ? (
        <InnerConfigureRound
          sessionRef={queryRef}
          membershipTypeId={membershipTypeId}
        />
      ) : null}
    </Suspense>
  );
};

type InnerConfigureRoundProps = {
  sessionRef: PreloadedQuery<ConfigureActivityManagementRoundSessionQuery>;
  membershipTypeId: string;
};

export const InnerConfigureRound = ({
  sessionRef,
  membershipTypeId,
}: InnerConfigureRoundProps) => {
  const data = usePreloadedQuery(ConfigureRoundSessionQueryString, sessionRef);
  const booking = data?.sharedBooking;

  const rate = useAtomValue(getRateOnRoundAtom);

  const roundId = booking?.rounds?.edges?.find(
    (edge) => edge?.node?.player?.id === booking?.ownerClient?.id
  )?.node?.id;

  useInitializeRound({
    bookingRef: booking ?? null,
    roundId: roundId ?? "",
    playerId: data?.session?.currentClientProfile?.member?.id ?? "",
  });

  return (
    <VStack gap={4}>
      <JoiningUserRow
        firstName={
          data?.session?.currentClientProfile?.member?.firstName ?? "Unknown"
        }
        lastName={
          data?.session?.currentClientProfile?.member?.lastName ?? "User"
        }
      />
      <HStack gap={1}>
        <ConfigureActivityManagementRoundPlayerRate
          bookingRef={(booking as any) ?? null}
        />
      </HStack>
      <RateVerificationBanner
        rateId={rate?.id ?? ""}
        membershipTypeId={membershipTypeId}
      />
    </VStack>
  );
};

const ConfigureRoundSessionQueryString = graphql`
  query ConfigureActivityManagementRoundSessionQuery(
    $shareId: String!
    $duration: Int
    $playerCount: Int
  ) {
    session {
      currentClientProfile {
        member {
          ... on Member {
            id
            firstName
            lastName
          }
          ... on Customer {
            id
            firstName
            lastName
          }
        }
      }
    }
    sharedBooking(shareId: $shareId) {
      ... on Booking {
        ownerClient {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
        }
        ...ConfigureActivityManagementRoundPlayerRate_Booking
        ...useInitializeRound_Booking
        rounds {
          edges {
            node {
              id
              player {
                id
              }
            }
          }
        }
      }
    }
  }
`;
